/**
 * Rich Markdown Renderer for AI-generated content using Streamdown
 * Streamdown is a Vercel library specifically designed for streaming markdown
 * with built-in support for incomplete markdown blocks and proper memoization
 *
 * Enhancements:
 * - GitHub Flavored Markdown (GFM) support via remark-gfm
 * - Math equations support via remark-math + rehype-katex
 * - Syntax highlighting via rehype-highlight
 * - Optimized memoization for streaming performance
 */

'use client'

import React, { useMemo } from 'react'
import { Streamdown } from 'streamdown'
import { cn } from '@/lib/utils'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import rehypeHighlight from 'rehype-highlight'

// Import KaTeX CSS for math rendering
import 'katex/dist/katex.min.css'
// Import highlight.js theme for syntax highlighting
import 'highlight.js/styles/github-dark.css'

interface MarkdownRendererProps {
  content: string
  onTimestampClick?: (timestamp: string) => void
  className?: string
  isStreaming?: boolean
}

export const MarkdownRenderer = React.memo(function MarkdownRenderer({
  content,
  className,
  isStreaming = false
}: MarkdownRendererProps) {
  if (!content) {
    return <p className="text-muted-foreground italic">No content yet...</p>
  }

  // Normalize markdown to ensure proper spacing around headers
  const normalizeMarkdown = (text: string): string => {
    // Since our backend prompts now properly format markdown,
    // we only need minimal normalization
    let normalized = text
      // Clean up excessive newlines (more than 2 in a row)
      .replace(/\n{3,}/g, '\n\n')
      // Ensure headers have space before them (if not at start)
      .replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2')
      // Trim start and end
      .trim()

    return normalized
  }

  // Memoize processed content to avoid recalculating on every render
  const processedContent = useMemo(() => normalizeMarkdown(content), [content])

  // Memoize plugin configuration for performance
  const remarkPlugins = useMemo(() => [remarkGfm, remarkMath], [])
  const rehypePlugins = useMemo(() => [rehypeKatex, rehypeHighlight], [])

  return (
    <Streamdown
      className={cn("prose prose-sm max-w-none dark:prose-invert", className)}
      isAnimating={isStreaming}
      parseIncompleteMarkdown={true}
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={{
        // Headings
        h1: ({ children, ...props }) => (
          <h1 {...props} className="text-2xl font-bold mt-8 mb-4 first:mt-0">
            {children}
          </h1>
        ),
        h2: ({ children, ...props }) => (
          <h2 {...props} className="text-xl font-bold mt-6 mb-3 first:mt-0">
            {children}
          </h2>
        ),
        h3: ({ children, ...props }) => (
          <h3 {...props} className="text-lg font-semibold mt-5 mb-2 first:mt-0">
            {children}
          </h3>
        ),
        h4: ({ children, ...props }) => (
          <h4 {...props} className="text-base font-semibold mt-4 mb-2 first:mt-0">
            {children}
          </h4>
        ),
        h5: ({ children, ...props }) => (
          <h5 {...props} className="text-sm font-semibold mt-3 mb-1 first:mt-0">
            {children}
          </h5>
        ),
        h6: ({ children, ...props }) => (
          <h6 {...props} className="text-sm font-medium mt-3 mb-1 first:mt-0 text-gray-600 dark:text-gray-400">
            {children}
          </h6>
        ),

        // Paragraphs
        p: ({ children, ...props }) => (
          <p {...props} className="mb-3 leading-relaxed">
            {children}
          </p>
        ),

        // Lists
        ul: ({ children, ...props }) => (
          <ul {...props} className="space-y-2 my-4 list-none">
            {children}
          </ul>
        ),
        ol: ({ children, ...props }) => (
          <ol {...props} className="space-y-2 my-4 ml-6 list-decimal list-outside">
            {children}
          </ol>
        ),
        li: ({ children, ...props }) => {
          // Check if this is inside an ordered list
          const isOrderedList = props.className?.includes('ordered-list-item')

          if (isOrderedList) {
            return (
              <li {...props} className="ml-2 leading-relaxed pl-2">
                {children}
              </li>
            )
          }

          return (
            <li {...props} className="ml-6 leading-relaxed flex items-start gap-2">
              <span className="text-orange-600 mt-1 flex-shrink-0">•</span>
              <span className="flex-1">{children}</span>
            </li>
          )
        },

        // Text formatting
        strong: ({ children, ...props }) => (
          <strong {...props} className="font-semibold text-gray-900 dark:text-gray-100">
            {children}
          </strong>
        ),
        em: ({ children, ...props }) => (
          <em {...props} className="italic text-gray-700 dark:text-gray-300">
            {children}
          </em>
        ),

        // Code
        code: ({ children, ...props }) => {
          // Check if this is inline code (not in a pre block)
          const isInline = !props.className?.includes('language-')

          if (isInline) {
            return (
              <code {...props} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-sm font-mono text-orange-600 dark:text-orange-400">
                {children}
              </code>
            )
          }

          // For code blocks, rehype-highlight will handle the styling
          return (
            <code {...props} className={cn("font-mono text-sm", props.className)}>
              {children}
            </code>
          )
        },
        pre: ({ children, ...props }) => (
          <pre {...props} className="my-4 p-4 rounded-lg bg-gray-900 dark:bg-gray-950 overflow-x-auto relative group">
            {children}
          </pre>
        ),

        // Links
        a: ({ children, ...props }) => (
          <a
            {...props}
            className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 underline underline-offset-2 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ),

        // Blockquotes
        blockquote: ({ children, ...props }) => (
          <blockquote {...props} className="my-4 pl-4 border-l-4 border-orange-600 italic text-gray-700 dark:text-gray-300">
            {children}
          </blockquote>
        ),

        // Horizontal Rule
        hr: ({ ...props }) => (
          <hr {...props} className="my-6 border-t border-gray-200 dark:border-gray-700" />
        ),

        // Tables
        table: ({ children, ...props }) => (
          <div className="my-4 overflow-x-auto">
            <table {...props} className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
              {children}
            </table>
          </div>
        ),
        thead: ({ children, ...props }) => (
          <thead {...props} className="bg-gray-50 dark:bg-gray-800">
            {children}
          </thead>
        ),
        tbody: ({ children, ...props }) => (
          <tbody {...props} className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
            {children}
          </tbody>
        ),
        tr: ({ children, ...props }) => (
          <tr {...props}>
            {children}
          </tr>
        ),
        th: ({ children, ...props }) => (
          <th {...props} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
            {children}
          </th>
        ),
        td: ({ children, ...props }) => (
          <td {...props} className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
            {children}
          </td>
        )
      }}
    >
      {processedContent}
    </Streamdown>
  )
})
